import type { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import type { PrefetchOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import { useRouter } from 'next/navigation'
import { useEffect, useMemo } from 'react'

import { useLocale } from '@src/i18n/LocaleProvider'
import { getLocalizedUrl } from '@src/i18n/locale.util'
import useGeolocationStore from '@src/stores/geolocationStore'
import { log } from '@src/utils/log.util'

interface NavigateOptionsLocalized extends NavigateOptions {
  target?: '_self' | '_blank'
  hard?: boolean
}

export function useLocalizedRouter() {
  const { locale } = useLocale()
  const router = useRouter()

  const setCountry = useGeolocationStore((state) => state.setCountry)

  useEffect(() => {
    setCountry(locale.countryCode)
  }, [locale.countryCode, setCountry])

  return useMemo(
    () => ({
      ...router,
      push(url: string, options?: NavigateOptionsLocalized | undefined) {
        if (options?.target === '_blank') {
          window.open(getLocalizedUrl(url, locale), '_blank')
        } else if (options?.hard) {
          log.info(`🔗 Using hard navigation to clear Next.js Router cache: ${url}`)
          window.location.href = getLocalizedUrl(url, locale)
        } else {
          router.push(getLocalizedUrl(url, locale), options)
        }
      },
      replace(url: string, options?: NavigateOptions | undefined) {
        router.replace(getLocalizedUrl(url, locale), options)
      },
      prefetch(url: string, options?: NavigateOptions | undefined) {
        router.prefetch(getLocalizedUrl(url, locale), options as PrefetchOptions)
      },
    }),
    [router, locale]
  )
}
